// ─── Public Status Page — the day grid ("Overblik") ──────────────────
//
// One cell per day, oldest → newest, with TODAY always flush right. The page
// renders one of these at page scale (size="lg", with the legend) and one per
// component inside ComponentGroupList (size="sm") — roughly 28 strips of up to
// 90 cells each.
//
// ── Responsive by MEASUREMENT, not by media query ───────────────────
// How many days fit is a function of this strip's own pixel width, and a strip
// is not the viewport: the same page renders it at ~860 px inside the overview
// card and at ~350 px inside a component row on the same screen. A
// `@media (max-width: …)` cannot express that (and the design guide bans new
// ones anyway), so the track measures itself with a ResizeObserver
// (lib/useElementWidth) and drops days from the OLDEST end until the rest fit.
// ~90 days on a laptop, ~40 on a 390 px phone, never fewer than 30.
//
// ── 2,500 cells with ZERO per-cell listeners ────────────────────────
// Pointer events are DELEGATED on the track: one `pointermove` finds the nearest
// `[data-d]` with `closest()`, reads the cell's day index, measures that ONE
// node and positions the single portalled tooltip. Explicitly NOT a native
// `title=` attribute per cell — 2,500 of those are slow to parse, appear only
// after a ~700 ms hover delay, cannot be styled to match the page, and are
// completely unreachable on a touch device, which is where a status page is most
// often read.
//
// ── Accessibility ───────────────────────────────────────────────────
// The STRIP is the accessible unit: `role="img"` with a Danish summary of the
// whole row, one tab stop, and (in HistoryGridRow) a visually-hidden list of the
// days that were not normal. Arrow keys walk the days with the same tooltip a
// mouse gets, and each step is announced through the live region below.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { STATUS_HISTORY_NO_DATA, uptimeFromMinutes, type PublicStatusIncident } from '@pcp/shared';

import { da } from '../i18n/da';
import { dayFromHistoryIndex, fmtDate, fmtPercent } from '../lib/format';
import { dayParam } from '../lib/day';
import { isPlainLeftClick } from '../router';
import { NO_DATA_LEVEL, STATUS_LEVELS, levelForStatus, statusForHistoryChar, uptimeRatio, INFO_LEVEL } from '../lib/levels';
import { useElementWidth } from '../lib/useElementWidth';
import type { HistoryGridProps } from '../types';
import { VisuallyHidden } from '../ui/VisuallyHidden';
import { HistoryGridRow } from './HistoryGridRow';
import { HistoryTooltip, type HistoryTooltipAnchor } from './HistoryTooltip';

/** Narrowest a day cell may become before we start dropping days. */
const CELL_MIN = 6;
/** The gap between two cells, and the value the CSS `gap` must match. */
const CELL_GAP = 2;
/** Width reserved for a visible label column beside the strip. */
const LABEL_W = 210;
/** Below this the label moves ABOVE the strip and stops eating width. */
const STACK_MAX = 620;
/** Slack for sub-pixel rounding, so the last cell can never overflow the row. */
const TRACK_SLACK = 8;
/** Fewer than a month of history is not worth drawing — cells go below CELL_MIN
 *  instead, which is still readable as a block of colour. */
const MIN_DAYS = 30;

/**
 * Extra, OPTIONAL inputs beyond the shared HistoryGridProps contract.
 *
 * The tooltip can name the incidents that touched a component on a given day,
 * which is derived entirely client-side by intersecting the incidents already in
 * the payload with the day — no extra bytes on the wire. The base contract in
 * ../types.ts does not carry them (App.tsx and ComponentGroupList are owned
 * elsewhere), so they are optional: pass them and the tooltip lists incidents,
 * omit them and it shows the day and its status, which is what a StatusCast-era
 * reader expects anyway.
 */
export interface HistoryGridExtraProps extends HistoryGridProps {
  /** The component this strip belongs to; omit for the page-level roll-up. */
  componentId?: string;
  /** Incidents from the summary payload (`summary.incidents`). */
  incidents?: readonly PublicStatusIncident[];
  /**
   * Render `label` as a visible column beside the strip. Off by default because
   * the page already prints the component name in its own grid column
   * (`.sp-comp`); on, the measurement below reserves LABEL_W for it and the
   * label drops above the strip on a narrow container.
   */
  showLabel?: boolean;
  /**
   * Print the uptime figure in the legend row. Defaults to `showLegend`, which
   * is the historical behaviour; a caller that renders its own headline figure
   * over a longer period (the component detail page, which reports the full
   * window rather than the cropped strip) passes false to avoid printing two
   * different percentages for the same bar.
   */
  showUptime?: boolean;
}

const NO_TITLES: string[] = [];

/**
 * Force the history string to exactly `historyDays` characters. The contract
 * guarantees it, but this page must render whatever it is handed: a snapshot
 * written by an older deploy, or a payload truncated in transit, cannot be
 * allowed to shift every cell's date by one day. Excess is trimmed from the OLD
 * end and a short string is padded there — "today" is always the last character.
 */
function normalizeHistory(history: string, historyDays: number): string {
  const total = Math.max(0, Math.floor(Number(historyDays) || 0));
  if (total === 0) return '';
  const raw = String(history ?? '');
  if (raw.length === total) return raw;
  if (raw.length > total) return raw.slice(raw.length - total);
  return STATUS_HISTORY_NO_DATA.repeat(total - raw.length) + raw;
}

/**
 * The same trim-or-pad for a numeric series, so `downtime[i]` and `dayMinutes[i]`
 * keep describing `history[i]` even if a payload arrives mis-sized. Padding is
 * `fill` at the OLD end, matching normalizeHistory's NO_DATA padding: a day we
 * were sent nothing about contributes nothing.
 */
function normalizeSeries(
  series: readonly number[] | undefined,
  historyDays: number,
  fill: number,
): number[] | null {
  const total = Math.max(0, Math.floor(Number(historyDays) || 0));
  if (total === 0 || !Array.isArray(series) || series.length === 0) return null;
  if (series.length === total) return series as number[];
  if (series.length > total) return series.slice(series.length - total);
  return [...new Array<number>(total - series.length).fill(fill), ...series];
}

/** Minutes in an ordinary day — the pad value when a payload is short a day. */
const DAY_MINUTES = 24 * 60;

/** Local midnight → the same instant one day later. */
function dayBounds(day: Date): { start: number; end: number } {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  return { start, end: start + 24 * 60 * 60 * 1000 };
}

function toTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Titles of the incidents whose window overlaps this calendar day and (when a
 * component is given) that actually touched it.
 *
 * A maintenance window is announced ahead of time, so its SCHEDULED start is the
 * real beginning of the affected period; a still-open incident has no end yet
 * and therefore runs up to now.
 */
function incidentTitlesOnDay(
  incidents: readonly PublicStatusIncident[],
  componentId: string | undefined,
  day: Date,
): string[] {
  if (incidents.length === 0) return NO_TITLES;
  const { start, end } = dayBounds(day);
  const now = Date.now();
  const titles: string[] = [];
  for (const incident of incidents) {
    if (componentId && !(incident.components ?? []).some((component) => component.id === componentId)) continue;
    const from = toTime(incident.scheduledStartAt) ?? toTime(incident.occurredAt);
    if (from === null || from >= end) continue;
    const to = toTime(incident.resolvedAt) ?? toTime(incident.scheduledEndAt) ?? now;
    if (to < start) continue;
    titles.push(incident.title);
  }
  return titles;
}

/** What the tooltip is currently pinned to, and what put it there. */
interface Cursor {
  /** ABSOLUTE index into the normalized history (so it survives a resize). */
  index: number;
  anchor: HistoryTooltipAnchor;
  source: 'pointer' | 'keyboard';
}

function rectOf(node: Element): HistoryTooltipAnchor {
  const rect = node.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

export function HistoryGrid({
  history,
  info,
  hrefForDay,
  onOpenDay,
  historyDays,
  historyStartDate,
  downtime,
  dayMinutes,
  label,
  showLegend = false,
  size = 'sm',
  componentId,
  incidents,
  showLabel = false,
  showUptime,
}: HistoryGridExtraProps) {
  const [trackRef, width] = useElementWidth<HTMLDivElement>();
  const [cursor, setCursor] = useState<Cursor | null>(null);
  // Identity for the delegated handler's `data-c`. The component id when the
  // caller has one; the label is a good enough discriminator otherwise (the
  // page-level bar is the only grid without a component behind it).
  const rowId = componentId ?? label;

  const normalized = useMemo(() => normalizeHistory(history, historyDays), [history, historyDays]);
  const total = normalized.length;
  const normalizedDowntime = useMemo(
    () => normalizeSeries(downtime, historyDays, 0),
    [downtime, historyDays],
  );
  const normalizedDayMinutes = useMemo(
    () => normalizeSeries(dayMinutes, historyDays, DAY_MINUTES),
    [dayMinutes, historyDays],
  );

  const stacked = width > 0 && width < STACK_MAX;
  const visibleDays = useMemo(() => {
    if (total === 0 || width <= 0) return 0;
    const reserved = showLabel && !stacked ? LABEL_W : 0;
    const stripWidth = width - reserved - TRACK_SLACK;
    const fits = Math.floor((stripWidth + CELL_GAP) / (CELL_MIN + CELL_GAP));
    // Never more days than we have, and never fewer than a month (below that the
    // bar stops being a shape you can read at a glance).
    return Math.min(total, Math.max(Math.min(MIN_DAYS, total), fits));
  }, [total, width, stacked, showLabel]);

  const offset = total - visibleDays;
  const visible = visibleDays > 0 ? normalized.slice(offset) : '';
  // Sliced with the SAME offset so cell N of the strip and character N of the
  // mask are the same day. Padded at the OLD end like normalizeHistory does, in
  // case a generation sent a shorter mask than history (an older snapshot).
  const visibleInfo = useMemo(() => {
    if (!info || visibleDays <= 0) return undefined;
    const padded = info.length >= total ? info.slice(info.length - total) : info.padStart(total, '0');
    return padded.slice(offset);
  }, [info, total, offset, visibleDays]);

  // A resize re-slices the window, which invalidates the anchor rect we measured.
  useEffect(() => {
    setCursor(null);
  }, [visibleDays]);

  // ── Pointer delegation ──────────────────────────────────────────────
  // ONE handler for the whole track. `closest('[data-d]')` walks up from
  // whatever was actually under the pointer, so it works for the cells and
  // ignores the gaps between them.
  const pointTo = useCallback((target: EventTarget | null, source: Cursor['source']) => {
    const node = target instanceof Element ? target.closest<HTMLElement>('[data-d]') : null;
    if (!node) return;
    const index = Number(node.dataset.d);
    if (!Number.isFinite(index)) return;
    const anchor = rectOf(node);
    setCursor((prev) =>
      // Moving WITHIN one cell must not re-render: same day, same source, and the
      // cell has not moved, so the tooltip is already exactly where it belongs.
      prev && prev.index === index && prev.source === source && prev.anchor.top === anchor.top
        ? prev
        : { index, anchor, source },
    );
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => pointTo(event.target, 'pointer'),
    [pointTo],
  );
  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => pointTo(event.target, 'pointer'),
    [pointTo],
  );

  /** Absolute history index → the YYYY-MM-DD the /day route carries. Built from
   *  the SAME dayFromHistoryIndex the tooltip uses, so a cell, its tooltip and
   *  its page can never name different days. */
  const dayIndexToDate = useCallback(
    (index: number): string => {
      const day = dayFromHistoryIndex(historyStartDate, index);
      return day ? dayParam(day) : '';
    },
    [historyStartDate],
  );

  /** The href for one day's page, or null when there is nothing worth opening —
   *  an ordinary green day with no notice and no incident stays inert, so the
   *  bar does not become 90 links to empty pages. */
  const hrefForCell = useCallback(
    (index: number): string | null => {
      if (!hrefForDay) return null;
      const char = normalized[index];
      const marked = info?.[index] === '1';
      const eventful = (char && char !== 'o' && char !== '-') || marked;
      if (!eventful) return null;
      const date = dayIndexToDate(index);
      return date ? hrefForDay(date, componentId ?? null) : null;
    },
    [hrefForDay, normalized, info, dayIndexToDate, componentId],
  );

  /**
   * TWO-TAP on touch, one click with a mouse.
   *
   * A tap is the only way to read this bar on a phone — which is where a status
   * page is most often read — and the tooltip is what it opens. Now that an
   * eventful cell is a real anchor, the first tap would navigate away and that
   * reading affordance would be gone entirely. So on a COARSE pointer the first
   * tap only pins the tooltip; tapping the same cell again follows the link.
   *
   * A mouse is unaffected: hovering already shows the tooltip, so the click has
   * nothing to steal.
   */
  const handleTrackClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!event.isTrusted) return;
      const cell = event.target instanceof Element ? event.target.closest('[data-d]') : null;
      if (!cell) return;
      const index = Number(cell.getAttribute('data-d'));
      if (!Number.isFinite(index)) return;

      const coarse = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
      // TOUCH: the first tap only pins the tooltip. Tapping the same cell again
      // falls through to the navigation below.
      if (coarse && !(cursor && cursor.index === index)) {
        event.preventDefault();
        pointTo(event.target, 'pointer');
        return;
      }

      // An inert cell is a plain <span> — nothing to open.
      if (!(cell instanceof HTMLAnchorElement)) return;
      // Let the browser have anything that is not a plain left click, so
      // ⌘-click / middle-click / "open in new tab" keep working on a real href.
      if (!isPlainLeftClick(event)) return;
      if (!onOpenDay) return;
      // Otherwise navigate IN PAGE. Without this the href triggers a full
      // document load: the whole payload is re-fetched and the day page renders
      // its spinner, for a route the client can already answer from data in hand.
      event.preventDefault();
      onOpenDay(dayIndexToDate(index), componentId ?? null);
    },
    [cursor, pointTo, onOpenDay, dayIndexToDate, componentId],
  );
  /** Leaving the track clears a POINTER tooltip; a keyboard one survives. */
  const clearPointer = useCallback(() => {
    setCursor((prev) => (prev && prev.source === 'pointer' ? null : prev));
  }, []);
  // A TAP is a first-class way to read this bar — it is the only one a phone
  // has — so a touch tooltip stays up until the visitor touches something else,
  // rather than vanishing on pointerup. `pointerleave` never fires for touch,
  // hence the document-level dismissal below.
  const hasCursor = cursor !== null;
  useEffect(() => {
    if (!hasCursor) return;
    const dismissOutside = (event: globalThis.PointerEvent) => {
      const track = trackRef.current;
      if (track && event.target instanceof Node && track.contains(event.target)) return;
      setCursor(null);
    };
    // The anchor is a viewport rect, so any scroll or resize detaches the bubble
    // from its cell. Cheaper and steadier than re-measuring on every frame.
    const dismiss = () => setCursor(null);
    document.addEventListener('pointerdown', dismissOutside, true);
    window.addEventListener('scroll', dismiss, { passive: true, capture: true });
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [hasCursor, trackRef]);

  // ── Keyboard ────────────────────────────────────────────────────────
  const moveCursor = useCallback(
    (index: number) => {
      const node = trackRef.current?.querySelector<HTMLElement>(`[data-d="${index}"]`);
      if (!node) return;
      setCursor({ index, anchor: rectOf(node), source: 'keyboard' });
    },
    [trackRef],
  );

  const handleStripFocus = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      // Only on a REAL keyboard focus. Clicking the bar also focuses it, and
      // popping a tooltip open under the mouse pointer on click is noise.
      if (typeof event.currentTarget.matches === 'function' && !event.currentTarget.matches(':focus-visible')) return;
      if (total === 0) return;
      // Start on today — the right-hand end of the bar, which is where the eye is.
      moveCursor(total - 1);
    },
    [moveCursor, total],
  );

  const handleStripBlur = useCallback(() => {
    setCursor((prev) => (prev && prev.source === 'keyboard' ? null : prev));
  }, []);

  const handleStripKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (visibleDays === 0) return;
      const first = offset;
      const last = total - 1;
      const current = cursor ? cursor.index : last;
      let next: number | null = null;
      switch (event.key) {
        case 'ArrowLeft':
          next = Math.max(first, current - 1);
          break;
        case 'ArrowRight':
          next = Math.min(last, current + 1);
          break;
        case 'Home':
          next = first;
          break;
        case 'End':
          next = last;
          break;
        case 'Escape':
          setCursor(null);
          return;
        case 'Enter':
        case ' ':
          // The strip is role="img" with a single tab stop (90 focusable cells
          // per component would mean thousands of them), so activation happens
          // here rather than on a cell. `current` is the cursor, or today when
          // nothing has been arrowed to yet — the same day the strip announces
          // on focus.
          if (!onOpenDay) return;
          event.preventDefault();
          onOpenDay(dayIndexToDate(current), componentId ?? null);
          return;
        default:
          return;
      }
      // Arrow keys would otherwise scroll the page out from under the bar.
      event.preventDefault();
      moveCursor(next);
    },
    [cursor, moveCursor, offset, total, visibleDays, onOpenDay, dayIndexToDate, componentId],
  );

  // ── Derived read-outs ───────────────────────────────────────────────
  // The figure describes the DAYS ACTUALLY ON SCREEN, not the full window — the
  // strip crops from the old end to fit its container, and a percentage that
  // silently covered 90 days above a 45-day bar would be quietly wrong. So the
  // two numeric series are sliced by the very same `offset`.
  const ratio = useMemo(() => {
    if (!visible) return null;
    if (normalizedDowntime && normalizedDayMinutes) {
      return uptimeFromMinutes(
        visible,
        normalizedDowntime.slice(offset),
        normalizedDayMinutes.slice(offset),
      );
    }
    // No downtime series: a snapshot written before v3. Fall back to counting
    // whole days rather than dropping the figure — coarse, and it can only ever
    // UNDERSTATE how well we did, which is the safe direction for a status page.
    return uptimeRatio(visible);
  }, [visible, normalizedDowntime, normalizedDayMinutes, offset]);
  const uptimeText =
    ratio === null
      ? da.overviewNoData
      : // Locale-free on purpose (CLAUDE.md): the words are Danish, the number is
        // the reader's — "99,9 %" here, "99.9%" for a visitor on a US device.
        `${fmtPercent(ratio, 2)} ${da.overviewUptime}`;

  const tooltip = useMemo(() => {
    if (!cursor) return null;
    const day = dayFromHistoryIndex(historyStartDate, cursor.index);
    const level = levelForStatus(statusForHistoryChar(normalized.charAt(cursor.index)));
    const titles = day && incidents?.length ? incidentTitlesOnDay(incidents, componentId, day) : NO_TITLES;
    return { day: fmtDate(day), level, titles };
  }, [cursor, historyStartDate, normalized, incidents, componentId]);

  return (
    <div className="sg-wrap">
      <div
        ref={trackRef}
        className="sg-track"
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onClick={handleTrackClick}
        onPointerLeave={clearPointer}
        onPointerCancel={clearPointer}
      >
        {visibleDays > 0 ? (
          <HistoryGridRow
            rowId={rowId}
            label={label}
            showLabel={showLabel}
            stacked={stacked}
            labelWidth={LABEL_W}
            history={visible}
            info={visibleInfo}
            offset={offset}
            historyStartDate={historyStartDate}
            hrefForCell={hrefForDay ? hrefForCell : undefined}
            size={size}
            onStripFocus={handleStripFocus}
            onStripBlur={handleStripBlur}
            onStripKeyDown={handleStripKeyDown}
          />
        ) : (
          // Before the first measurement (and when there is no history at all):
          // an empty strip of the right height, so the row does not jump once the
          // real cell count is known.
          <div className={`sg-strip sg-strip--empty${size === 'lg' ? ' sg-strip--lg' : ''}`} aria-hidden="true" />
        )}
      </div>

      {showLegend && (
        <>
          <div className="sg-legend">
            <span>{da.overviewLegendOlder}</span>
            <span className="sg-legend-rule" />
            <span>{da.overviewLegendToday}</span>
          </div>
          <div className="sg-legend sg-legend--keys">
            <ul className="sg-swatches">
              {/* Information appears ONLY when this strip actually carries a
                  mask. The page-wide bar never does, so its legend keeps
                  explaining exactly the colours it shows and no more. */}
              {[...Object.values(STATUS_LEVELS), ...(info ? [INFO_LEVEL] : []), NO_DATA_LEVEL].map((level) => (
                <li className="sg-swatch" key={level.modifier}>
                  <span
                    className={`sg-swatch-dot${level === NO_DATA_LEVEL ? ' sg-swatch-dot--nodata' : ''}`}
                    // A swatch is a block, so it wears the block tier — the
                    // legend must match the strip it explains exactly.
                    style={{ ['--sg-c' as string]: level.fill } as CSSProperties}
                  />
                  {level.label}
                </li>
              ))}
            </ul>
            {showUptime ?? true ? <span className="sg-uptime">{uptimeText}</span> : null}
          </div>
        </>
      )}

      {/* Keyboard read-out. The tooltip itself is aria-hidden (its content would
          otherwise be read twice — the strip's aria-label already covers the
          row), so arrowing across the days announces here instead. */}
      <div role="status" aria-live="polite">
        {cursor?.source === 'keyboard' && tooltip && (
          <VisuallyHidden>
            {`${tooltip.day}: ${tooltip.level.label}${tooltip.titles.length ? `. ${tooltip.titles.join('. ')}` : ''}`}
          </VisuallyHidden>
        )}
      </div>

      {cursor && tooltip && (
        <HistoryTooltip
          anchor={cursor.anchor}
          title={label}
          day={tooltip.day}
          statusLabel={tooltip.level.label}
          statusColor={tooltip.level.fill}
          incidents={tooltip.titles}
        />
      )}
    </div>
  );
}
